import { createEffect, createSignal, onCleanup } from "solid-js";
import {
  useEnsureParticipant,
  useMaybeRoomContext,
} from "solid-livekit-components";

import {
  createAudioAnalyser,
  LocalAudioTrack,
  Participant,
  ParticipantEvent,
  RemoteAudioTrack,
  RoomEvent,
  Track,
} from "livekit-client";

import { useState } from "@revolt/state";
import {
  DEFAULT_INPUT_ACTIVATION_THRESHOLD,
  inputActivationThresholds,
} from "../../../../voice/voiceInputThreshold";

type AudioAnalyserHandle = ReturnType<typeof createAudioAnalyser>;

function getMicTrack(participant: Participant) {
  const publication = participant.getTrackPublication(Track.Source.Microphone);
  if (!publication?.track || publication.isMuted) return undefined;

  const track = publication.track;
  if (track instanceof LocalAudioTrack || track instanceof RemoteAudioTrack) {
    return track;
  }

  return undefined;
}

/**
 * Snappier speaking indicator than raw `useIsSpeaking` alone.
 * Uses client-side audio analysis when a mic track is available; falls back to LiveKit events.
 */
export function useResponsiveSpeaking(participant?: Participant) {
  const p = useEnsureParticipant(participant);
  const room = useMaybeRoomContext();
  const state = useState();
  const [speaking, setSpeaking] = createSignal(p.isSpeaking);

  createEffect(() => {
    const { speakOn, speakOff } = inputActivationThresholds(
      state.voice.inputActivationThreshold ??
        DEFAULT_INPUT_ACTIVATION_THRESHOLD,
    );

    let rafId: number | undefined;
    let analyser: AudioAnalyserHandle | undefined;
    let analyserActive = false;
    let aboveThreshold = false;

    const evaluateEvents = () => {
      if (analyserActive) return;

      const active = p.isSpeaking || p.audioLevel > speakOn;
      setSpeaking(active);
    };

    const stopAnalyser = () => {
      analyserActive = false;
      aboveThreshold = false;

      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = undefined;
      }

      if (analyser) {
        void analyser.cleanup();
        analyser = undefined;
      }
    };

    const startAnalyser = (track: LocalAudioTrack | RemoteAudioTrack) => {
      stopAnalyser();

      try {
        analyser = createAudioAnalyser(track, {
          cloneTrack: true,
          smoothingTimeConstant: 0.2,
        });
      } catch {
        evaluateEvents();
        return;
      }

      analyserActive = true;

      const tick = () => {
        if (!analyser) return;

        const volume = analyser.calculateVolume();
        const active = aboveThreshold ? volume > speakOff : volume > speakOn;
        aboveThreshold = active;
        setSpeaking(active);
        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
    };

    const syncAnalyser = () => {
      const track = getMicTrack(p);
      if (track) {
        startAnalyser(track);
      } else {
        stopAnalyser();
        evaluateEvents();
      }
    };

    p.on(ParticipantEvent.IsSpeakingChanged, evaluateEvents);
    p.on(ParticipantEvent.TrackMuted, syncAnalyser);
    p.on(ParticipantEvent.TrackUnmuted, syncAnalyser);
    p.on(ParticipantEvent.TrackPublished, syncAnalyser);
    p.on(ParticipantEvent.TrackUnpublished, syncAnalyser);
    p.on(ParticipantEvent.TrackSubscribed, syncAnalyser);
    p.on(ParticipantEvent.TrackUnsubscribed, syncAnalyser);
    p.on(ParticipantEvent.LocalTrackPublished, syncAnalyser);
    p.on(ParticipantEvent.LocalTrackUnpublished, syncAnalyser);

    syncAnalyser();

    const roomInstance = room?.();
    roomInstance?.on(RoomEvent.ActiveSpeakersChanged, evaluateEvents);

    onCleanup(() => {
      p.off(ParticipantEvent.IsSpeakingChanged, evaluateEvents);
      p.off(ParticipantEvent.TrackMuted, syncAnalyser);
      p.off(ParticipantEvent.TrackUnmuted, syncAnalyser);
      p.off(ParticipantEvent.TrackPublished, syncAnalyser);
      p.off(ParticipantEvent.TrackUnpublished, syncAnalyser);
      p.off(ParticipantEvent.TrackSubscribed, syncAnalyser);
      p.off(ParticipantEvent.TrackUnsubscribed, syncAnalyser);
      p.off(ParticipantEvent.LocalTrackPublished, syncAnalyser);
      p.off(ParticipantEvent.LocalTrackUnpublished, syncAnalyser);
      roomInstance?.off(RoomEvent.ActiveSpeakersChanged, evaluateEvents);
      stopAnalyser();
    });
  });

  return speaking;
}
